
import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { mapTransactionApiToUi } from "@/lib/mappers/transactions";
import { generateMockTransactions } from "@/lib/mock-transactions";
import type { Transaction } from "@/types/transaction";
import type { TransactionsApiResponse } from "@/types/api/transactions";

export interface GetTransactionsParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  transactionType?: string;
  status?: string;
  customerId?: string;
  dateFrom?: string;
  dateTo?: string;
  amountMin?: number;
  amountMax?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Hook to fetch transactions list data
 * Handles both Demo and Live modes
 */
export function useTransactionsList(params?: GetTransactionsParams) {
  const { apiGet } = useApi();
  const mockTransactions = generateMockTransactions();

  return useModeAwareQuery<Transaction[], Error>(
    ["transactions", params],
    async ({ signal }) => {
      const response = await apiGet<TransactionsApiResponse>("/api/v1/transactions", params, signal);
      return response.data.map(mapTransactionApiToUi);
    },
    mockTransactions,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Combined hook for transactions page data
 * Returns transactions list with loading and error states
 */
export function useTransactionsPageData(params?: GetTransactionsParams) {
  const transactionsQuery = useTransactionsList(params);

  return {
    transactions: transactionsQuery.data ?? [],
    isLoading: transactionsQuery.isLoading,
    error: transactionsQuery.error,
    refetch: () => {
      transactionsQuery.refetch();
    },
  };
}
